import { uploadFile } from '@/config/utils'
import type {
	FirmwareBundleResult,
	FirmwareType,
	FirmwareVersionDependency,
	UploadFirmwareResponse,
} from '../firmwaresApi'
import { demoApi } from './db'

export const firmwaresApi = {
	getFirmwares: async (organizationId?: string): Promise<FirmwareType[]> => {
		const path = organizationId ? `/v1/firmwares/${organizationId}` : '/v1/firmwares'
		return demoApi.get<FirmwareType[]>(path)
	},
	getFiles: async (): Promise<FirmwareType[]> => [],
	getComponentFirmwares: async (): Promise<FirmwareType[]> => demoApi.get('/v1/firmwares/components'),
	getFirmwareBundles: async (): Promise<FirmwareBundleResult> => demoApi.get('/v1/firmware-bundles'),
	uploadFirmware: async (
		_version: string,
		formData: FormData,
		onUploadProgress?: (progress: { loaded: number; total: number }) => void,
		signal?: AbortSignal,
	): Promise<UploadFirmwareResponse> => {
		const res = (await uploadFile(
			'/v1/firmwares',
			formData,
			onUploadProgress ? p => onUploadProgress({ loaded: p.loaded, total: p.total ?? 0 }) : () => {},
			signal,
		)) as { data: UploadFirmwareResponse }
		return res.data
	},
	createFirmware: async (firmwareData: FirmwareType): Promise<FirmwareType> =>
		demoApi.post('/v1/firmwares', firmwareData),
	createFirmwareBundle: async (
		firmwares: { component_name: string; version_number: string }[],
		version: string,
		file_name?: string,
	): Promise<{ id: string }> =>
		demoApi.post('/v1/firmware-bundles', {
			firmwares,
			version,
			file_name: file_name ?? 'bundle.zip',
		}),
	updateFirmwareMetadata: async (version: string, uncompressed_size: number): Promise<FirmwareType> =>
		demoApi.put(`/v1/firmwares/${encodeURIComponent(version)}`, {
			uncompressed_size,
		}),
	updateFirmwareBundleById: async (
		bundleId: string,
		bundleBody: {
			uncompressed_size: number
			upload_status: string
			content_length: number
		},
	): Promise<unknown> => demoApi.put(`/v1/firmware-bundles/id/${encodeURIComponent(bundleId)}`, bundleBody),
	updateFirmwareDependency: async (
		component: string,
		firmwareVersion: string,
		dependencies: FirmwareVersionDependency[],
	): Promise<{ device_component_name: string; version_number: string }> =>
		demoApi.put(`/v1/firmwares/component/${component}/${encodeURIComponent(firmwareVersion)}`, { dependencies }),
	deleteFirmwareBundleById: async (bundleId: string): Promise<unknown> =>
		demoApi.delete(`/v1/firmware-bundles/id/${encodeURIComponent(bundleId)}`),
	deleteFirmware: async (firmwareId: string): Promise<void> => demoApi.delete(`/v1/firmwares/${firmwareId}`),
	deleteFile: async (fileId: string): Promise<void> => demoApi.delete(`/v1/files/${fileId}`),
	downloadFile: async (version: string): Promise<Blob> =>
		demoApi.get<Blob>(`/v1/files/${encodeURIComponent(version)}/artifact`),
	activateFirmware: async (version: string, deactivate: boolean): Promise<FirmwareType> =>
		demoApi.put(`/v1/firmwares/${encodeURIComponent(version)}`, { is_deactivated: deactivate }),
	activateComponentFirmware: async (
		component: string,
		firmwareVersion: string,
		deactivate: boolean,
	): Promise<FirmwareType> =>
		demoApi.put(`/v1/firmwares/component/${component}/${encodeURIComponent(firmwareVersion)}`, {
			is_deactivated: deactivate,
		}),
	downloadFirmware: async (version: string): Promise<Blob> =>
		demoApi.get<Blob>(`/v1/firmwares/${encodeURIComponent(version)}/artifact`),
	downloadComponentFirmware: async (component: string, version: string): Promise<Blob> =>
		demoApi.get<Blob>(`/v1/firmwares/component/${component}/${encodeURIComponent(version)}/artifact`),
	downloadFirmwareBundleById: async (bundleId: string): Promise<Blob> =>
		demoApi.get<Blob>(`/v1/firmware-bundles/${encodeURIComponent(bundleId)}/artifact`),
}
